<template>
  <div class="mins-stats-wrapper">
    <div class="cal-header">
      <div class="cal-nav">
        <button type="button" class="cal-nav-button" @click="changeMonth(-1)" aria-label="Previous month">
          <i class="bi bi-chevron-left" aria-hidden="true"></i>
        </button>
        <span class="cal-month">{{ getMonthName(minsData.month) }} {{ minsData.year }}</span>
        <button type="button" class="cal-nav-button" @click="changeMonth(1)" aria-label="Next month">
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <b-button size="sm" variant="outline-primary" :disabled="loadingTrends" @click="showTrendsGraph()">
        <i class="bi bi-graph-up" aria-hidden="true"></i>
        {{ loadingTrends ? "Loading…" : "View trends" }}
      </b-button>
    </div>

    <p v-if="loadingMins" class="cal-loading">Loading minutes…</p>

    <div class="cal-grid" v-else>
      <div class="cal-dow" v-for="dow in dayHeaders" :key="dow">{{ dow }}</div>
      <div class="cal-dow cal-dow--total">Week total</div>

      <template v-for="(week, weekIndex) in calendarWeeks" :key="`week-${weekIndex}`">
        <div v-for="cell in week.days" :key="cell.key" class="cal-cell" :class="{
          'cal-cell--blank': !cell.inMonth,
          'cal-cell--today': cell.inMonth && cell.isToday,
          'cal-cell--has-data': cell.inMonth && cell.day.minsTotal !== null,
        }" :tabindex="cell.inMonth ? 0 : -1" :role="cell.inMonth ? 'button' : null"
          @click="cell.inMonth && onCellClick(cell.day)"
          @keydown.enter.prevent="cell.inMonth && onCellClick(cell.day)"
          @keydown.space.prevent="cell.inMonth && onCellClick(cell.day)">
          <template v-if="cell.inMonth">
            <span class="cal-date">{{ cell.dateNum }}</span>
            <template v-if="cell.day.minsTotal !== null">
              <span class="cal-mins">{{ cell.day.minsTotal }}<small> min</small></span>
              <span v-if="cell.day.sessionCount > 1" class="cal-sessions">{{ cell.day.sessionCount }} sessions</span>
            </template>
            <span v-else class="cal-add" aria-hidden="true"><i class="bi bi-plus"></i></span>
          </template>
        </div>

        <div class="cal-week-total" :class="`load--${loadLevel(week.total)}`" v-b-tooltip.hover
          :title="loadTooltip(week.total)">
          {{ week.total }}
        </div>
      </template>
    </div>

    <div class="cal-footer" v-if="!loadingMins">
      <div class="month-total">
        Month total: <strong>{{ monthlyTotal }}</strong> mins
      </div>
      <div class="cal-legend">
        <span class="legend-chip load--ok">On target ≤ {{ LOAD_HIGH }}</span>
        <span class="legend-chip load--high">High {{ LOAD_HIGH + 1 }}–{{ LOAD_OVER }}</span>
        <span class="legend-chip load--over">Over {{ LOAD_OVER }}+</span>
        <span class="legend-note">weekly minutes</span>
      </div>
    </div>

    <!-- Day stats add/edit -->
    <b-modal v-if="showAddDayStatsBreakDown" v-model="showAddDayStatsBreakDown" size="xl" :title="dayModalTitle"
      hide-footer>
      <form class="day-stats-form" @submit.prevent="saveDayStats()">
        <p class="day-stats-player">{{ selectedUserData.name }}</p>

        <div class="form__item">
          <label class="form__label" for="ds_session_type">Session name</label>
          <input class="form__input" type="text" id="ds_session_type" v-model="selectedDayPlayerData.session_type"
            placeholder="e.g. Session, Gym, Match" />
        </div>

        <div v-if="under16 === false" class="day-stats-grid">
          <div class="form__item">
            <label class="form__label" for="ds_session_mins">Session minutes</label>
            <div class="input-with-unit">
              <input class="form__input" type="number" min="0" inputmode="numeric" id="ds_session_mins"
                v-model="selectedDayPlayerData.session_mins" />
              <span class="unit">min</span>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_session_distance">Distance covered</label>
            <div class="input-with-unit">
              <input class="form__input" type="number" min="0" inputmode="numeric" id="ds_session_distance"
                v-model="selectedDayPlayerData.session_distance" />
              <span class="unit">m</span>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_hsr">High-speed running distance</label>
            <div class="input-with-unit">
              <input class="form__input" type="number" min="0" inputmode="numeric" id="ds_hsr"
                v-model="selectedDayPlayerData.total_absolute_high_speed_distance" />
              <span class="unit">m</span>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_mpm">Meterage per minute</label>
            <div class="input-with-unit">
              <input class="form__input" type="number" min="0" step="any" inputmode="decimal" id="ds_mpm"
                v-model="selectedDayPlayerData.meterage_per_minute" />
              <span class="unit">m/min</span>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_max_vel">Top speed (max vel)</label>
            <input class="form__input" type="number" min="0" step="any" inputmode="decimal" id="ds_max_vel"
              v-model="selectedDayPlayerData.max_vel_max" />
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_max_velocity">Maximum velocity</label>
            <div class="input-with-unit">
              <input class="form__input" type="number" min="0" step="any" inputmode="decimal" id="ds_max_velocity"
                v-model="selectedDayPlayerData.maximum_velocity" />
              <span class="unit">%</span>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_accels">Accelerations (total efforts)</label>
            <input class="form__input" type="number" min="0" inputmode="numeric" id="ds_accels"
              v-model="selectedDayPlayerData.accelerations_total_efforts" />
          </div>

          <div class="form__item">
            <label class="form__label" for="ds_decels">Decelerations (total efforts)</label>
            <input class="form__input" type="number" min="0" inputmode="numeric" id="ds_decels"
              v-model="selectedDayPlayerData.decelerations_total_efforts" />
          </div>
        </div>

        <div class="day-stats-actions">
          <b-button type="submit" variant="success">
            {{ editDayPlayerData ? "Save changes" : "Add session" }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <!-- Season minutes trend -->
    <b-modal v-if="showTrendsModal" v-model="showTrendsModal" size="xl" title="Season minutes trend" hide-footer>
      <div v-if="playerLoadChartDataSet.labels && playerLoadChartDataSet.labels.length > 0">
        <Bar-Chart :chartData="playerLoadChartDataSet" />
      </div>
      <p v-else class="cal-loading">No session data recorded for this season yet.</p>
    </b-modal>
  </div>
</template>

<script>
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import extendSeasonEndDateToFullYearMixin from "@/mixin/extendSeasonEndDateToFullYear.js";
import BarChart from "@/components/BarChart.vue";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Weekly minutes-load thresholds (mins per Mon–Sun week).
const LOAD_HIGH = 1300;
const LOAD_OVER = 1400;

export default {
  components: {
    BarChart,
  },
  data() {
    return {
      LOAD_HIGH,
      LOAD_OVER,
      dayHeaders: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      editDayPlayerData: false,
      showAddDayStatsBreakDown: false,
      showTrendsModal: false,
      loadingMins: false,
      loadingTrends: false,
      minsData: {
        year: dayjs().year(),
        month: dayjs().month(),
        weekDays: [],
      },
      playerDailyMins: [],
      playerPlayMakerGpsData: [],
      selectedDayPlayerData: null,
      allPlayerLoadReporsData: [],
      playerLoadChartDataSet: {},
    };
  },
  mixins: [extendSeasonEndDateToFullYearMixin],
  props: {
    selectedUserValue: {
      type: Number,
      required: true,
    },
    selectedUserData: {
      type: Object,
      required: true,
    },
    under16: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:personalBestAndAverages", "openDayStatsModal"],
  computed: {
    optimizedPlayerData() {
      if (!this.playerDailyMins?.length) return [];

      return this.playerDailyMins.map((session) => ({
        ...session,
        sessionDate: dayjs(session.session_date),
        sessionMins: parseInt(session.session_mins) || 0,
      }));
    },
    monthlyTotal() {
      const monthStart = dayjs()
        .year(this.minsData.year)
        .month(this.minsData.month)
        .startOf("month");
      const monthEnd = monthStart.endOf("month");
      return this.optimizedPlayerData.reduce(
        (sum, session) =>
          session.sessionDate.isSameOrAfter(monthStart, "day") &&
            session.sessionDate.isSameOrBefore(monthEnd, "day")
            ? sum + session.sessionMins
            : sum,
        0,
      );
    },
    // The month as Mon–Sun rows with leading/trailing blanks, each row
    // carrying its week's minutes total (sessions from adjacent months in an
    // overlapping week count towards that week, matching load monitoring).
    calendarWeeks() {
      const days = this.minsData.weekDays;
      if (!days.length) return [];

      const cells = [];
      const leading = (dayjs(days[0].date).day() + 6) % 7; // Mon = 0
      for (let i = 0; i < leading; i++) {
        cells.push({ inMonth: false, key: `lead-${i}` });
      }
      days.forEach((day) => {
        const d = dayjs(day.date);
        cells.push({
          inMonth: true,
          key: d.format("YYYY-MM-DD"),
          day,
          dateNum: d.date(),
          isToday: d.isSame(dayjs(), "day"),
        });
      });
      while (cells.length % 7 !== 0) {
        cells.push({ inMonth: false, key: `tail-${cells.length}` });
      }

      const weeks = [];
      for (let i = 0; i < cells.length; i += 7) {
        const weekCells = cells.slice(i, i + 7);
        const anchor = weekCells.find((cell) => cell.inMonth);
        let total = 0;
        if (anchor) {
          const anchorDate = dayjs(anchor.day.date);
          const weekStart = anchorDate.subtract((anchorDate.day() + 6) % 7, "day");
          const weekEnd = weekStart.add(6, "day");
          total = this.optimizedPlayerData.reduce(
            (sum, session) =>
              session.sessionDate.isSameOrAfter(weekStart, "day") &&
                session.sessionDate.isSameOrBefore(weekEnd, "day")
                ? sum + session.sessionMins
                : sum,
            0,
          );
        }
        weeks.push({ days: weekCells, total });
      }
      return weeks;
    },
    dayModalTitle() {
      const date =
        this.selectedDayPlayerData && this.selectedDayPlayerData.session_date
          ? dayjs(this.selectedDayPlayerData.session_date).format("ddd D MMM YYYY")
          : "";
      return `${this.editDayPlayerData ? "Edit stats" : "Add stats"} — ${date}`;
    },
  },
  mounted() {
    this.minsData.weekDays = this.populateMinsCal(
      this.minsData.year,
      this.minsData.month,
    );
    this.loadPlayerMinsPerformanceData();
  },
  methods: {
    loadLevel(total) {
      if (total > LOAD_OVER) return "over";
      if (total > LOAD_HIGH) return "high";
      return "ok";
    },
    loadTooltip(total) {
      const label = {
        ok: "On target",
        high: "High load",
        over: "Over the weekly load limit",
      }[this.loadLevel(total)];
      return `${total} mins this week — ${label}`;
    },
    getMonthName(monthIndex) {
      return dayjs().month(monthIndex).format("MMMM");
    },
    changeMonth(delta) {
      const next = dayjs()
        .year(this.minsData.year)
        .month(this.minsData.month)
        .date(1)
        .add(delta, "month");
      this.minsData.weekDays = this.populateMinsCal(next.year(), next.month());
      this.loadPlayerMinsPerformanceData();
    },
    populateMinsCal(year, month) {
      this.minsData.year = year;
      this.minsData.month = month;
      const date = new Date(year, month, 1);
      const dates = [];

      while (date.getMonth() === month) {
        dates.push({
          date: new Date(date),
          id: null,
          minsTotal: null,
        });
        date.setDate(date.getDate() + 1);
      }

      return dates;
    },
    async loadPlayerMinsPerformanceData() {
      this.loadingMins = true;

      if (this.under16 === true) {
        const playmakerResponse = await PlayerPhysicalPerformance.find({
          action: "getPlayerMinsPerformancePlayeMakerData",
          player_id: this.selectedUserValue,
          season_start_date: localStorage.getItem(
            "currentPlayingseasonStartDate",
          ),
          season_end_date: this.extendSeasonEndDateToFullYear(),
        }).catch(() => null);

        if (playmakerResponse !== null && playmakerResponse.status === 200) {
          this.playerPlayMakerGpsData = playmakerResponse.data;
          if (this.playerPlayMakerGpsData.length > 0) {
            this.$emit(
              "update:personalBestAndAverages",
              this.playerPlayMakerGpsData,
              "Playmaker",
            );
          }
        }
      }

      const responce = await PlayerPhysicalPerformance.find({
        action: "getPlayerMinsPerformanceData",
        player_id: this.selectedUserValue,
        season_start_date: localStorage.getItem("currentPlayingseasonStartDate"),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      }).catch(() => null);

      this.loadingMins = false;

      if (responce === null || responce.status !== 200) {
        this.$toast.error("Could not load minutes data. Please try again.", {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
        return;
      }

      this.playerDailyMins = responce.data;
      if (this.playerDailyMins.length > 0) {
        this.$emit(
          "update:personalBestAndAverages",
          this.playerDailyMins,
          "PlayerMins",
        );
      }
      this.populatePlayerMinsPerformanceData();
    },
    populatePlayerMinsPerformanceData() {
      const sessionsByDate = new Map();
      this.playerDailyMins.forEach((dayData) => {
        const date = dayData.session_date;
        if (!sessionsByDate.has(date)) {
          sessionsByDate.set(date, []);
        }
        sessionsByDate.get(date).push(dayData);
      });

      const weekDayIndexMap = new Map();
      this.minsData.weekDays.forEach((day, index) => {
        weekDayIndexMap.set(dayjs(day.date).format("YYYY-MM-DD"), index);
      });

      sessionsByDate.forEach((sessions, date) => {
        const index = weekDayIndexMap.get(date);
        if (index === undefined) return;

        // The cell shows the DAY total; editing targets the main session
        // ("Session" type when there are several, otherwise the only one).
        const dayTotal = sessions.reduce(
          (sum, s) => sum + (parseInt(s.session_mins) || 0),
          0,
        );
        const mainSession =
          sessions.find((s) => s.session_type === "Session") || sessions[0];

        const updatedWeekDay = {
          ...this.minsData.weekDays[index],
          ...mainSession,
          minsTotal: dayTotal,
          sessionCount: sessions.length,
        };
        this.minsData.weekDays.splice(index, 1, updatedWeekDay);
      });
    },
    onCellClick(day) {
      if (day.minsTotal !== null) {
        // Clone so typing in the modal can't mutate the visible calendar,
        // and cancelling leaves no phantom edits behind.
        this.editDayPlayerData = true;
        this.selectedDayPlayerData = { ...day };
      } else {
        this.editDayPlayerData = false;
        this.selectedDayPlayerData = {
          total_absolute_high_speed_distance: 0,
          id: 0,
          maximum_velocity: 0,
          player_id: this.selectedUserValue,
          playing_down_level: 0,
          playing_up_level: 0,
          session_date: dayjs(day.date).format("YYYY-MM-DD"),
          session_distance: 0,
          session_mins: 0,
          session_type: "",
          max_vel_max: 0,
          accelerations_total_efforts: 0,
          decelerations_total_efforts: 0,
          meterage_per_minute: 0,
        };
      }
      this.showAddDayStatsBreakDown = true;
    },
    saveDayStats() {
      if (this.editDayPlayerData === true) {
        this.editDayMinsStatsData();
      } else {
        this.addDayMinsStatsData();
      }
    },
    async editDayMinsStatsData() {
      const responce = await PlayerPhysicalPerformance.find({
        action: "editDayMinsStatsData",
        ...this.selectedDayPlayerData,
      }).catch(() => null);

      if (responce !== null && responce.status === 200) {
        this.loadPlayerMinsPerformanceData();
        this.showAddDayStatsBreakDown = false;
        this.$toast.success("Day stats updated.", {
          title: "Saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
      } else {
        this.$toast.error("The stats could not be saved. Please try again.", {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
      }
    },
    async addDayMinsStatsData() {
      const responce = await PlayerPhysicalPerformance.find({
        action: "addDayMinsStatsData",
        data: this.selectedDayPlayerData,
      }).catch(() => null);

      if (responce !== null && responce.status === 200) {
        this.loadPlayerMinsPerformanceData();
        this.showAddDayStatsBreakDown = false;
        this.$toast.success("Day stats added.", {
          title: "Saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
      } else {
        this.$toast.error("The stats could not be saved. Please try again.", {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
      }
    },
    async showTrendsGraph() {
      this.loadingTrends = true;
      const responce = await PlayerPhysicalPerformance.find({
        action: "loadAllPlayerReporsData",
        player_id: this.selectedUserData.id,
        season_start_date: localStorage.getItem("currentPlayingseasonStartDate"),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      }).catch(() => null);
      this.loadingTrends = false;

      if (responce === null || responce.status !== 200) {
        this.$toast.error("Could not load the season trend data.", {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
        });
        return;
      }

      this.allPlayerLoadReporsData = responce.data;
      this.barChartDataForLoad();
      this.showTrendsModal = true;
    },
    barChartDataForLoad() {
      const chartData = this.allPlayerLoadReporsData;
      if (!chartData?.length) {
        this.playerLoadChartDataSet = { labels: [], datasets: [] };
        return;
      }

      const labels = [];
      const data = [];
      chartData.forEach((item) => {
        labels.push(dayjs(item.session_date).format("DD MMM"));
        data.push(parseInt(item.session_mins) || 0);
      });

      this.playerLoadChartDataSet = {
        labels,
        datasets: [
          {
            label: "Session mins",
            borderColor: "rgba(50, 115, 220, 0.5)",
            backgroundColor: "rgba(50, 115, 220, 0.4)",
            data,
          },
        ],
      };
    },
  },
};
</script>
<style scoped lang="scss">
.mins-stats-wrapper {
  padding: 1rem 1.25rem 1.5rem;
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.cal-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cal-nav-button {
  background: #fff;
  border: 1px solid #c3cdd5;
  border-radius: 4px;
  padding: 0.3rem 0.65rem;
  cursor: pointer;
  line-height: 1;
  color: #35424c;

  i {
    color: #35424c;
    font-size: 1rem;
  }

  &:hover,
  &:focus-visible {
    background: #dbe4ea;
    border-color: #9fb0bc;

    i {
      color: #1d2830;
    }
  }
}

.cal-month {
  font-weight: 700;
  font-size: 1.05rem;
  min-width: 10.5rem;
  text-align: center;
}

.cal-loading {
  color: #5b6770;
  margin: 1.5rem 0;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr)) 5.5rem;
  gap: 2px;
}

.cal-dow {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5b6770;
  padding: 0.35rem 0.25rem;
  text-align: center;

  &.cal-dow--total {
    color: #35424c;
  }
}

.cal-cell {
  min-height: 4.25rem;
  border: 1px solid #dfe6ea;
  border-radius: 4px;
  padding: 0.3rem 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  background: #fff;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: #7fa8c9;
    background: #f4f8fb;

    .cal-add {
      opacity: 1;
    }
  }

  &.cal-cell--blank {
    background: transparent;
    border-color: transparent;
    cursor: default;
  }

  &.cal-cell--today {
    border-color: #3273dc;
    box-shadow: inset 0 0 0 1px #3273dc;
  }

  &.cal-cell--has-data {
    background: #f2f9f3;
  }
}

.cal-date {
  font-size: 0.75rem;
  color: #5b6770;
}

.cal-mins {
  font-size: 1rem;
  font-weight: 700;

  small {
    font-weight: 400;
    color: #5b6770;
  }
}

.cal-sessions {
  font-size: 0.7rem;
  color: #5b6770;
}

.cal-add {
  opacity: 0;
  color: #7fa8c9;
  font-size: 1.1rem;
  margin: auto;
}

.cal-week-total {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.load--ok {
  background: #e6f4ea;
  color: #1d7a3a;
}

.load--high {
  background: #fdf3df;
  color: #8a5c00;
}

.load--over {
  background: #fdecee;
  color: #c03540;
}

.cal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.month-total {
  color: #35424c;
}

.cal-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;

  .legend-chip {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
  }

  .legend-note {
    font-size: 0.75rem;
    color: #5b6770;
  }
}

/* Day stats form */
.day-stats-player {
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.day-stats-form {
  .form__item {
    margin-bottom: 0.75rem;
  }

  .form__label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
}

.day-stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0 1.25rem;
}

@media screen and (min-width: 768px) {
  .day-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (min-width: 1200px) {
  .day-stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.input-with-unit {
  display: flex;
  align-items: center;
  gap: 0.4rem;

  .form__input {
    flex: 1;
    min-width: 0;
  }

  .unit {
    color: #5b6770;
    font-size: 0.85rem;
    white-space: nowrap;
  }
}

.day-stats-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

@media screen and (max-width: 640px) {
  .cal-grid {
    grid-template-columns: repeat(7, minmax(0, 1fr)) 3.25rem;
  }

  .cal-cell {
    min-height: 3.25rem;
    padding: 0.2rem;
  }

  .cal-mins {
    font-size: 0.8rem;

    small {
      display: none;
    }
  }

  .cal-sessions {
    display: none;
  }
}
</style>
